// libraries
import React from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import { Stack } from '@mui/material_modern'

// state
import { setSnackBar } from 'state/ui'

// queries & mutations
import { useCreatePaymentTermMutation } from 'services/paymentTerms'

// generic components
import Input from 'components/generic/form/Input'

// helper & utils
import { trackEvent } from 'utils/tracking'
import { CHURN_ZERO_EVENTS as cz } from 'utils/constants'
import { formOptions } from 'utils/validation/paymentTerm'
import DialogModal from '.'

const NewPaymentTermModal = ({ open, onSave, ...restProps }) => {
  const dispatch = useDispatch()
  const {
    control,
    getValues,
    formState: { isDirty, isValid },
    reset,
  } = useForm(formOptions)
  const [createPaymentTerm, { isLoading: isLoadingCreatePaymentTerm }] = useCreatePaymentTermMutation()

  const handleSuccess = newItem => {
    dispatch(
      setSnackBar({
        snackbarType: 'success',
        snackbarMessage: 'Changes Saved',
      }),
    )
    trackEvent({ eventName: cz.CREATE_PAYMENT_TERM })
    onSave(newItem.id)
    reset()
  }

  const handleError = error => {
    const errorMsg = error?.data?.error || 'There was an error adding the payment term'
    trackEvent({
      eventName: cz.CREATE_PAYMENT_TERM,
      errorMsg,
    })
    dispatch(
      setSnackBar({
        snackbarType: 'error',
        snackbarMessage: errorMsg,
      }),
    )
  }

  const handleOnSave = async () => {
    const data = getValues()
    const newItem = {
      display: data.paymentTermName,
      days: parseInt(data.paymentTermDays, 10),
      active: true,
    }
    await createPaymentTerm({ paymentTerm: newItem })
      .unwrap()
      .then(item => {
        handleSuccess(item)
      })
      .catch(error => {
        handleError(error)
      })
  }

  return (
    <DialogModal
      open={open}
      {...restProps}
      onSave={handleOnSave}
      saveDisabled={isLoadingCreatePaymentTerm || !isDirty || !isValid}
    >
      <Stack direction="row" spacing={2} pt={1}>
        <Input name="paymentTermName" label="Display Name" defaultValue="" control={control} />
        <Input
          name="paymentTermDays"
          label="Term Length in Days"
          defaultValue=""
          type="number"
          pattern="[0-9]*"
          control={control}
        />
      </Stack>
    </DialogModal>
  )
}

NewPaymentTermModal.propTypes = {
  ...DialogModal.propTypes,
  open: PropTypes.bool.isRequired,
  onSave: PropTypes.func,
}

NewPaymentTermModal.defaultProps = {
  ...DialogModal.defaultProps,
  title: 'New Payment Term',
  buttonTitle: 'Add Payment Term',
  hideDividers: true,
  dialogStyles: {
    '& .MuiDialog-paper': { width: '100%', maxWidth: 600 },
  },
}

export default NewPaymentTermModal
